// PowerSync local-first dispatch for `cancelSession`.
//
// Sibling to capture-client-powersync-create-session.ts and the last
// remaining capture mutation to migrate off the REST-only path. Before
// this dispatch existed, tapping Cancel while offline left the dialog
// stuck in its "submitting" state indefinitely because the REST adapter
// retried the DELETE forever in the background.
//
// Flow:
//   1. Read the session row's building_id from the local mirror so the
//      caller's result envelope (used by site/building-capture screens
//      to navigate after cancel) carries the right building reference.
//   2. Mirror the server's cascade locally: DELETE the capture_items
//      tied to the session first, then DELETE the capture_sessions
//      row. Two reasons for the local cascade:
//        a. The screens' reactive useQuery() bindings re-render off
//           the local state — without the items DELETE, the
//           building-capture screen would briefly show captured
//           assets that no longer belong to any session.
//        b. evictLocalSession() in use-cancel-session.ts already does
//           this cleanup after every successful dispatch — keeping
//           the same SQL shape here means the eviction call becomes
//           a no-op (zero rows affected) rather than queuing duplicate
//           CRUD entries.
//   3. Return buildingStatus=null. The buildings sync stream is the
//      source of truth for that column; the existing REST transport
//      already returns null on its 404 idempotent-success branch
//      (capture-client-transport.ts:168), so downstream consumers
//      handle the null case. A PR #537-style optimistic
//      ps_data__buildings update would be a nice-to-have follow-up
//      if the cancel-then-immediately-renavigate UX shows stale
//      status; users land on /capture/sites/:id after cancel so the
//      window is small.
//   4. Emit a `session-cancelled` event with the optimistic shape.
//
// The connector (connector-dispatch-session-cancel.ts) picks up the
// SDK CRUD DELETE entry and POSTs DELETE /capture/sessions/:id with
// the session's pre-delete syncVersion (carried through previousValues
// per the schema's trackPrevious config). 404 + 409 are both treated
// as idempotent success there — the user's intent ("this session is
// no longer mine to capture against") is satisfied in either case.

import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { eventForResult } from './capture-client-events'
import type { CaptureEventBus } from './capture-events'

import type { PowerSyncDb } from './capture-client-powersync'

interface SessionLookupRow {
  buildingId: string | null
}

export async function dispatchCancelSession(
  mutation: Extract<CaptureMutation, { kind: 'cancelSession' }>,
  db: PowerSyncDb,
  bus: CaptureEventBus,
): Promise<CaptureMutationResult> {
  // 1. Read building_id off the local row before we delete it. Missing
  // local row → buildingId=null (treat the cancel as effectively a
  // site-scoped, status-irrelevant operation; the connector's
  // subsequent DELETE will still POST and the server returns whatever
  // it actually has).
  const sessionRows = await db.getAll<SessionLookupRow>(
    `SELECT building_id AS buildingId FROM capture_sessions WHERE id = ? LIMIT 1`,
    [mutation.sessionId],
  )
  const buildingId = sessionRows[0]?.buildingId ?? null

  // 2. Cascade locally. capture_items first so a defensive consumer
  // querying mid-delete never sees orphan items pointing at a session
  // that's already gone.
  await db.execute(
    `DELETE FROM capture_items WHERE capture_session_id = ?`,
    [mutation.sessionId],
  )
  await db.execute(
    `DELETE FROM capture_sessions WHERE id = ?`,
    [mutation.sessionId],
  )

  // 3. Assemble the result. buildingStatus=null matches the existing
  // REST transport's 404 path — downstream callers (use-cancel-session)
  // already tolerate null.
  const result: CaptureMutationResult = {
    kind: 'cancelSession',
    sessionId: mutation.sessionId,
    buildingId,
    buildingStatus: null,
  }
  bus.emit(eventForResult(mutation, result))
  return result
}
